import { useState, useEffect, type ChangeEvent } from 'react';
import { Button, Input, Spin } from 'antd';
import { CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import * as api from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { useToast } from './Toast';

/**
 * Only http(s) values become a clickable link. A `url`-rendered property is
 * agent-writable, and a `javascript:` or `data:` value in an href would run on
 * click; the value itself still shows in the text field either way.
 */
function isFollowableUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

function supportsCopyButton(definition: api.TaskPropertyDefinition): boolean {
  return definition.render === 'identifier' || definition.key === 'execution.local_directory';
}

function isTaskPropertyReadOnly(task: api.Task, definition: api.TaskPropertyDefinition): boolean {
  return (
    definition.read_only === true ||
    (definition.key === 'execution.local_directory' &&
      task.properties['execution.workspace_source'] === 'repository_mapping')
  );
}

function isTaskPropertyVisible(task: api.Task, definition: api.TaskPropertyDefinition): boolean {
  return !(
    definition.key === 'execution.local_directory' &&
    task.properties['execution.workspace_source'] === 'repository_mapping'
  );
}

/**
 * Editor for a task's properties — the key-value references a task carries
 * (PR link, issue link, agent session id).
 *
 * Keys come from the server's registry rather than a list hardcoded here, so a
 * property registered server-side shows up without a client release. Each key
 * saves on its own PUT/DELETE the moment it is committed: there is no
 * whole-object write to lose a value another writer set on a different key
 * while this panel was open.
 *
 * Each write's response is the authority for what this editor shows. The
 * surrounding modal keeps the task it was opened with, so reading from the prop
 * would show a property vanishing the instant it was saved.
 */
export function TaskPropertiesEditor({
  task,
  onChanged,
}: {
  task: api.Task;
  /** Notify the surrounding list that the task changed. Must not unmount this editor. */
  onChanged: () => void;
}) {
  const { showToast } = useToast();
  const [definitions, setDefinitions] = useState<api.TaskPropertyDefinition[] | null>(null);
  const [properties, setProperties] = useState<Record<string, string>>(task.properties);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [copyFailedKey, setCopyFailedKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.fetchTaskPropertyDefinitions().then(
      (loaded) => {
        if (!cancelled) setDefinitions(loaded);
      },
      // A registry that failed to load leaves the section empty rather than
      // breaking the rest of the task editor.
      () => {
        if (!cancelled) setDefinitions([]);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const clearDraft = (key: string) =>
    setDrafts((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });

  const commit = async (key: string, value: string) => {
    const trimmed = value.trim();
    if (trimmed === (properties[key] ?? '')) {
      // Drop the draft too. A draft that outlives its row (here, or after the
      // row is deleted) re-opens the field pre-filled with a value the user
      // already discarded, and the next blur writes it back.
      clearDraft(key);
      setAddingKey(null);
      return;
    }
    setSavingKey(key);
    setFailedKey(null);
    try {
      const updated = trimmed
        ? await api.setTaskProperty(task.id, key, trimmed)
        : await api.deleteTaskProperty(task.id, key);
      setProperties(updated.properties);
      clearDraft(key);
      setAddingKey(null);
      onChanged();
    } catch {
      // Keep the typed value on screen and say the write failed, rather than
      // leaving a value that looks saved and is not.
      setFailedKey(key);
    } finally {
      setSavingKey(null);
    }
  };

  const remove = async (key: string) => {
    setSavingKey(key);
    setFailedKey(null);
    try {
      const updated = await api.deleteTaskProperty(task.id, key);
      setProperties(updated.properties);
      clearDraft(key);
      onChanged();
    } catch {
      setFailedKey(key);
    } finally {
      setSavingKey(null);
    }
  };

  const copyValue = async (key: string, value: string) => {
    setCopiedKey(null);
    setCopyFailedKey(null);
    if (await copyToClipboard(value)) {
      setCopiedKey(key);
      showToast('已复制');
      return;
    }
    setCopyFailedKey(key);
    showToast('复制失败');
  };

  if (definitions === null) {
    return (
      <div style={{ marginTop: 12 }}>
        <Spin size="small" />
      </div>
    );
  }

  const visibleDefinitions = definitions.filter((definition) =>
    isTaskPropertyVisible(task, definition),
  );
  const set = visibleDefinitions.filter(
    (d) => properties[d.key] !== undefined || d.key === addingKey,
  );
  const unset = visibleDefinitions.filter(
    (d) =>
      !isTaskPropertyReadOnly(task, d) && properties[d.key] === undefined && d.key !== addingKey,
  );
  const handleDraftChange = (key: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setDrafts((current) => ({ ...current, [key]: event.target.value }));
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Properties</div>
      {set.map((definition) => {
        const stored = properties[definition.key] ?? '';
        const draft = drafts[definition.key];
        const readOnly = isTaskPropertyReadOnly(task, definition);
        return (
          <div
            key={definition.key}
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}
          >
            <span style={{ fontSize: 12, color: '#888', minWidth: 96 }}>{definition.label}</span>
            <Input
              size="small"
              value={draft ?? stored}
              disabled={savingKey === definition.key || readOnly}
              onChange={handleDraftChange(definition.key)}
              onBlur={() => {
                if (!readOnly) void commit(definition.key, draft ?? stored);
              }}
              onPressEnter={() => {
                if (!readOnly) void commit(definition.key, draft ?? stored);
              }}
              placeholder={definition.render === 'url' ? 'https://…' : ''}
            />
            {definition.render === 'url' && isFollowableUrl(stored) && (
              <a href={stored} target="_blank" rel="noreferrer noopener" style={{ fontSize: 12 }}>
                Open
              </a>
            )}
            {stored && supportsCopyButton(definition) && (
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                disabled={savingKey === definition.key}
                onClick={() => void copyValue(definition.key, stored)}
                aria-label={
                  copiedKey === definition.key
                    ? `Copied ${definition.label}`
                    : `Copy ${definition.label}`
                }
                title={
                  copiedKey === definition.key
                    ? `Copied ${definition.label}`
                    : `Copy ${definition.label}`
                }
              />
            )}
            {stored && !readOnly && (
              <Button
                size="small"
                type="text"
                icon={<DeleteOutlined />}
                disabled={savingKey === definition.key}
                onClick={() => remove(definition.key)}
              />
            )}
            {failedKey === definition.key && (
              <span style={{ fontSize: 12, color: '#ff4d4f' }}>Save failed</span>
            )}
            {copyFailedKey === definition.key && (
              <span style={{ fontSize: 12, color: '#ff4d4f' }}>Copy failed</span>
            )}
          </div>
        );
      })}
      {unset.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {unset.map((definition) => (
            <Button
              key={definition.key}
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setAddingKey(definition.key)}
            >
              {definition.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
